// src/pages/product/ProductDetailPage.tsx
// Auto-generated by corebuilder. Edit freely.

import { useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useProduct, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Alert,
  ErrorAlert,
} from '@react/components';

/**
 * Product detail page.
 */
export const ProductDetailPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const store = useProduct();
  const { t } = useI18n();
  const item = store.currentItem;

  useEffect(() => {
    if (id) {
      store.GetProduct(id);
    }
    return () => {
      store.clearCurrent();
    };
  }, [id]);
  const handleDelete = async () => {
    if (!id || !confirm(t('product.deleteConfirm'))) return;
    try {
      await store.DeleteProduct(id);
      navigate('/products');
    } catch {
      // Error is handled by store
    }
  };

  if (store.isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  if (store.error) {
    return (
      <>
        <ErrorAlert error={store.error} />
        <Link to="/products" className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </>
    );
  }
  if (!item) {
    return (
      <Alert variant="warning" title={t('common.notFound')}>
        <Link to="/products" className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <nav className="text-sm mb-2">
            <Link to="/products" className="text-muted-foreground hover:text-foreground">
              {t('product.list')}
            </Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <span className="text-foreground">{t('product.details')}</span>
          </nav>
          {/* The tab already names this page; the heading stays for screen readers. */}
          <h1 className="sr-only">{t('product.details')}</h1>
        </div>
        <div className="flex gap-2">
          <Link to={`/products/${id}/edit`}>
            <Button variant="secondary">{t('common.edit')}</Button>
          </Link>
          <Button variant="danger" onClick={handleDelete}>
            {t('common.delete')}
          </Button>
        </div>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>{t('product.details')}</CardTitle>
          <CardDescription>{t('product.title')}</CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Entity</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.entity ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Slug</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.slug ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Name</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.name ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Description</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.description ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Tagline</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.tagline ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Owner</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.owner ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Legal Entity</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.legal_entity ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Legal Entity Id</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.legal_entity_id ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Status</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.status ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Is Builtin</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.is_builtin ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Is Public</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.is_public ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Capabilities</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.capabilities ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Resource Types</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.resource_types ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Icon Url</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.icon_url ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Logo Url</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.logo_url ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Color Primary</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.color_primary ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Color Secondary</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.color_secondary ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Web Url</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.web_url ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Api Base Url</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.api_base_url ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Docs Url</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.docs_url ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Support Url</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.support_url ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Oauth2 Client Id</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.oauth2_client_id ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Allowed Scopes</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.allowed_scopes ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Redirect Uris</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.redirect_uris ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Allowed Grant Types</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.allowed_grant_types ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Resources</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.resources ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Aspects</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.aspects ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Metadata</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.metadata ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Created At</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.created_at ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Updated At</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.updated_at ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Published At</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.published_at ?? '')}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>
    </div>
  );
});

export default ProductDetailPage;
